import { gql } from '@apollo/client';

/**
 * Content → Authors and the article byline (Prototype v17, backlog 53b14acc):
 * who writes the site's articles. People add and change them here; agents do
 * the same over MCP (save_author). An author's page is /authors/<slug> on the
 * site; bylines on articles link to it.
 */

export interface Author {
  id: string;
  slug: string;
  name: string;
  role: string | null;
  bio: string | null;
  photoUrl: string | null;
  topics: string[];
  socials: Partial<Record<SocialKey, string>>;
  email: string | null;
  path: string;
  articleCount?: number;
  liveCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthorArticle {
  id: string;
  title: string;
  status: 'live' | 'scheduled' | 'draft';
  /** Waiting on a yes, by the same rule as the Website list and Today. */
  awaitingApproval?: boolean | null;
  date: string | null;
  coAuthor: boolean;
}

export type SocialKey = 'x' | 'linkedin' | 'threads' | 'instagram' | 'site';

/** The social profiles an author page links, as the design lists them: key, label, address prefix, handle prefix. */
export const SOCIALS: Array<[SocialKey, string, string, string]> = [
  ['x', 'X', 'x.com/', '@'],
  ['linkedin', 'LinkedIn', 'linkedin.com/in/', ''],
  ['threads', 'Threads', 'threads.net/@', '@'],
  ['instagram', 'Instagram', 'instagram.com/', '@'],
  ['site', 'Website', 'https://', ''],
];

export const GET_TINYCMS_AUTHORS = gql`
  query TinycmsAuthors($accountId: ID!) {
    tinycmsAuthors(accountId: $accountId)
  }
`;

export const GET_TINYCMS_AUTHOR = gql`
  query TinycmsAuthor($accountId: ID!, $id: ID!) {
    tinycmsAuthor(accountId: $accountId, id: $id)
  }
`;

export const CREATE_TINYCMS_AUTHOR = gql`
  mutation CreateTinycmsAuthor($accountId: ID!, $input: JSON!) {
    createTinycmsAuthor(accountId: $accountId, input: $input)
  }
`;

export const UPDATE_TINYCMS_AUTHOR = gql`
  mutation UpdateTinycmsAuthor($accountId: ID!, $id: ID!, $input: JSON!) {
    updateTinycmsAuthor(accountId: $accountId, id: $id, input: $input)
  }
`;

/** Remove an author no article credits (refused otherwise). App only: agents can't delete authors. */
export const DELETE_TINYCMS_AUTHOR = gql`
  mutation DeleteTinycmsAuthor($accountId: ID!, $id: ID!) {
    deleteTinycmsAuthor(accountId: $accountId, id: $id)
  }
`;

export const initials = (name: string) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * The profile checklist (Prototype v17): what search engines and readers look
 * for. Each { ok, label, hint }.
 */
export function profileChecks(a: Author) {
  const socials = SOCIALS.filter(([k]) => a.socials?.[k]).length;
  return [
    {
      ok: Boolean(a.photoUrl),
      label: 'Photo',
      hint: 'Readers trust a face. Shown next to every byline.',
    },
    {
      ok: Boolean(a.role),
      label: 'Role or title',
      hint: 'For example, “College football analyst”.',
    },
    {
      ok: (a.bio || '').length >= 80,
      label: 'Bio of two sentences or more',
      hint: 'Say what they cover and why they know it.',
    },
    {
      ok: socials > 0,
      label: 'At least one social profile',
      hint: 'Lets search engines confirm who this is.',
    },
    {
      ok: (a.liveCount || 0) > 0,
      label: 'At least one published article',
      hint: 'The author page lists them.',
    },
  ];
}

/** How a profile reads: "@haitran_cards", "linkedin.com/in/haitran", or the site's address. */
export function socialShown(key: SocialKey, value: string | undefined) {
  if (!value) return null;
  const spec = SOCIALS.find(([k]) => k === key)!;
  return key === 'site'
    ? value.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : `${spec[3]}${value}`;
}

/** "Hai Tran", "Hai Tran and Dana Ruiz", "A, B and C". */
export function joinNames(names: string[]): string {
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0] || '';
}

/**
 * What search engines read about who wrote it, as the design words it:
 * "Structured data: Article by Hai Tran (Person, 2 social profiles) · published
 * 2026-10-10". Nothing when there's no byline.
 */
export function structuredDataLine(
  ids: string[],
  byId: Map<string, Author>,
  publishedIso: string | null
): { text: string; warn: boolean } | null {
  const people = ids.map((id) => byId.get(id)).filter((a): a is Author => Boolean(a));
  // No line: "+ Add author" and Checks already say it (backlog b23a8dd3).
  if (!people.length) return null;
  const socials = SOCIALS.filter(([k]) => people[0].socials?.[k]).length;
  const who =
    people.length === 1
      ? `Person, ${socials} social ${socials === 1 ? 'profile' : 'profiles'}`
      : `${people.length} Person entries, each linked to their author page`;
  return {
    text: `Structured data: Article by ${joinNames(people.map((a) => a.name))} (${who}) · published ${
      publishedIso ? publishedIso.slice(0, 10) : 'on publish'
    }`,
    warn: false,
  };
}

/** A just-uploaded photo, read back for its address (Authors → Photo). */
export const AUTHOR_PHOTO_ASSET = gql`
  query AuthorPhotoAsset($accountId: ID!, $id: ID!) {
    mediaAsset(accountId: $accountId, id: $id) {
      id
      sha256
      originalFilename
      renditions {
        kind
        url
        isCurrent
      }
    }
  }
`;
